// นักเรียนที่ลา (ภาคศาสนา) — หัวหน้าวิชาการศาสนา (pos 57)
//
// รวมการลาทุกทางที่มีผลกับนักเรียนภาคศาสนา:
//   tb_student_leave       ครูแจ้งลา (ครูประจำชั้นสามัญ / ฝ่ายปกครอง / วิชาการ / ครูประจำชั้นศาสนา)
//   tb_home_leave_request  ใบกลับบ้านที่ฝ่ายปกครองอนุมัติ
//
// ไม่กรองตาม source เพราะคำถามคือ "ช่วงนี้ใครไม่อยู่บ้าง" ไม่ใช่ "ฝ่ายไหนเป็นคนแจ้ง" —
// เห็นเฉพาะของฝ่ายตัวเองก็ตอบคำถามนี้ไม่ได้ (เหตุผลเดียวกับหน้าแจ้งลาของครูประจำชั้นศาสนา)
//
// อ่านอย่างเดียว การแจ้ง/ยกเลิกการลาเป็นหน้าที่ของครูประจำชั้นและฝ่ายปกครอง
import React, { useEffect, useMemo, useState } from 'react'
import { Badge, Card, Col, Form, Table } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import axiosInstance from '../services/axiosInterceptor'
import { useLoading } from '../contexts/LoadingContext'
import { useLanguage } from '../contexts/LanguageContext'
import { StudentName, studentDisplayName } from '../utils/studentName'
import th from '../locales/islamic/th'
import jawi from '../locales/islamic/jawi'
import {
  API, AcademicFilterBar, DateRangeBar, StatTile, StatusDot, StudentAvatar,
  alertRequestError, roomLabel, thaiDate, useAcademicFilters, useDarkTables, useDateRange,
  useStudyYears,
} from './academicShared'

const EMPTY = { range: { start: '', end: '' }, rows: [], byType: {}, summary: { leaves: 0, students: 0, days: 0 } }

// ประเภทการลา — ป่วย/กิจมาจาก tb_student_leave.leave_type ส่วน "กลับบ้าน" เป็นใบ
// คนละตาราง ที่รวมมาไว้ที่นี่ ใช้สีคนละโทนเพื่อให้แยกออกว่าต้องไปแก้ที่หน้าไหน
const TYPES = [
  { key: 'sick',     color: '#dc3545' },
  { key: 'personal', color: '#0dcaf0' },
  { key: 'home',     color: '#6f42c1' },
]

// ฝ่ายที่แจ้ง — ตรงกับ enum ของ tb_student_leave.source บวก 'home_leave' ที่โมเดล
// ใส่ให้ใบกลับบ้าน เรียงตามความถี่ที่เจอจริง (ครูประจำชั้นสามัญแจ้งมากที่สุด)
const SOURCES = ['advisor', 'islamic_advisor', 'discipline', 'academic', 'home_leave']

// ผู้แจ้ง = ชื่อครู + ฝ่ายที่แจ้ง
//
// ชื่ออย่างเดียวไม่พอ เพราะสี่ฝ่ายแจ้งลงตารางเดียวกัน (advisor / islamic_advisor /
// discipline / academic) และครูคนหนึ่งเป็นได้ทั้งครูประจำชั้นสามัญและครูประจำชั้นศาสนา
// เห็นแต่ชื่อจึงตอบไม่ได้ว่าใบนี้มาจากฝั่งไหน ซึ่งเป็นสิ่งที่ต้องรู้เวลาจะไปตามเรื่อง
//
// ใบกลับบ้านไม่มีคอลัมน์ผู้แจ้งแบบเดียวกัน (ฝ่ายปกครองเป็นผู้อนุมัติเสมอ) จึงเหลือ
// แต่ป้ายฝ่าย ไม่ใช่ขีดกลางเปล่า ๆ ที่อ่านเหมือนข้อมูลหาย
const ReportedBy = ({ t, row }) => {
  const dept = t.academic.leave.source[row.source]
  return (
    <div style={{ minWidth: 0 }}>
      {row.reported_by
        ? <div className="text-truncate">{row.reported_by}</div>
        : <div className="text-secondary">{t.academic.leave.source[row.source] || '-'}</div>}
      {dept && row.reported_by && (
        <Badge bg="light" text="dark" className="fs-xs fw-normal border">{dept}</Badge>
      )}
    </div>
  )
}

export default function IslamicStudentsOnLeave() {
  const { setLoading } = useLoading()
  const { lang } = useLanguage()
  const t = lang === 'th' ? th : jawi
  const navigate = useNavigate()
  useDarkTables()

  const { years, yearId, setYearId } = useStudyYears()
  const filters = useAcademicFilters(yearId, t)
  const range = useDateRange('week')
  const [data, setData] = useState(EMPTY)
  const [type, setType] = useState('')
  const [source, setSource] = useState('')
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (!yearId) return
    let alive = true
    setLoading(true)
    const q = new URLSearchParams(filters.query.replace(/^\?/, ''))
    q.set('start', range.start)
    q.set('end', range.end)
    axiosInstance.get(`${API}/islamic/academic/leave/${yearId}?${q}`)
      .then(res => { if (alive) setData(res.data || EMPTY) })
      .catch(err => { if (alive) { setData(EMPTY); alertRequestError(t, err, t.common.error) } })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [yearId, filters.query, range.start, range.end, setLoading, t])

  const { summary, byType, rows } = data

  // นับจำนวนใบของแต่ละฝ่ายเอง ไม่ให้ server ส่งมา เพราะค่านี้เปลี่ยนตามตัวกรองประเภท
  // ที่ผู้ใช้เลือกอยู่ ถ้าเอาจาก server จะค้างเป็นยอดของทั้งช่วงแล้วไม่ตรงกับตารางที่เห็น
  const sourceCounts = useMemo(() => {
    const out = {}
    for (const r of rows) if (!type || r.leave_type === type) out[r.source] = (out[r.source] || 0) + 1
    return out
  }, [rows, type])

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    return rows.filter(r => {
      if (type && r.leave_type !== type) return false
      if (source && r.source !== source) return false
      if (!q) return true
      return [r.st_name, r.st_arabicname, r.st_idnumber, r.leave_note, r.reported_by]
        .some(v => String(v ?? '').toLowerCase().includes(q))
    })
  }, [rows, type, source, search])

  const dateText = (r) =>
    r.leave_start === r.leave_end
      ? thaiDate(r.leave_start)
      : `${thaiDate(r.leave_start)} – ${r.open_ended ? t.academic.leave.openEnded : thaiDate(r.leave_end)}`

  return (
    <React.Fragment>
      <div className="mb-3">
        <ol className="breadcrumb fs-sm mb-1">
          <li className="breadcrumb-item">{t.academic.groupTitle}</li>
          <li className="breadcrumb-item active">{t.academic.leave.pageTitle}</li>
        </ol>
        <h4 className="main-title mb-0">{t.academic.leave.pageTitle}</h4>
      </div>

      <Card className="card-one mb-3">
        <Card.Body>
          <DateRangeBar t={t} range={range} />
          <hr className="my-3" />
          <AcademicFilterBar t={t} years={years} yearId={yearId} setYearId={setYearId} filters={filters}>
            <Col xs={6} md={3} lg={2}>
              <Form.Label className="fs-xs text-secondary mb-1">{t.academic.leave.filterType}</Form.Label>
              <Form.Select size="sm" value={type} onChange={e => setType(e.target.value)}>
                <option value="">{t.academic.leave.allTypes}</option>
                {TYPES.map(x => (
                  <option key={x.key} value={x.key}>
                    {t.academic.leave.type[x.key]}{byType[x.key] ? ` (${byType[x.key]})` : ''}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6} md={3} lg={2}>
              <Form.Label className="fs-xs text-secondary mb-1">{t.academic.leave.filterSource}</Form.Label>
              <Form.Select size="sm" value={source} onChange={e => setSource(e.target.value)}>
                <option value="">{t.academic.leave.allSources}</option>
                {SOURCES.filter(s => sourceCounts[s]).map(s => (
                  <option key={s} value={s}>{t.academic.leave.source[s]} ({sourceCounts[s]})</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={12} md={6} lg={3}>
              <Form.Label className="fs-xs text-secondary mb-1">{t.academic.leave.search}</Form.Label>
              <Form.Control
                size="sm"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={t.academic.leave.searchPlaceholder}
              />
            </Col>
          </AcademicFilterBar>
        </Card.Body>
      </Card>

      <div className="row g-2 mb-3">
        <Col xs={6} md={3}>
          <StatTile label={t.academic.leave.statLeaves} value={summary.leaves.toLocaleString('th-TH')} />
        </Col>
        <Col xs={6} md={3}>
          <StatTile label={t.academic.leave.statStudents} value={summary.students.toLocaleString('th-TH')} />
        </Col>
        <Col xs={6} md={3}>
          <StatTile
            label={t.academic.leave.statDays}
            value={summary.days.toLocaleString('th-TH')}
            sub={t.academic.leave.daysHint}
          />
        </Col>
        <Col xs={6} md={3}>
          <Card className="card-one h-100">
            <Card.Body className="p-3">
              <div className="fs-xs text-secondary">{t.academic.leave.statByType}</div>
              <div className="mt-2">
                {TYPES.filter(x => byType[x.key]).map(x => (
                  <div key={x.key} className="d-flex align-items-center gap-2 fs-sm">
                    <StatusDot color={x.color} size={8} />
                    <span className="flex-grow-1">{t.academic.leave.type[x.key]}</span>
                    <span className="fw-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>{byType[x.key]}</span>
                  </div>
                ))}
                {TYPES.every(x => !byType[x.key]) && <div className="fs-sm text-secondary">-</div>}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </div>

      <Card className="card-one">
        <Card.Header className="d-flex flex-wrap align-items-center gap-2">
          <Card.Title as="h6" className="mb-0">{t.academic.leave.listTitle}</Card.Title>
          <Badge bg="secondary" className="fs-xs">{t.academic.leave.leaveCount(visible.length)}</Badge>
        </Card.Header>
        <Card.Body className="p-0">
          {visible.length === 0 ? (
            <div className="text-center text-secondary py-5 fs-sm">{t.academic.leave.empty}</div>
          ) : (
            <div className="table-responsive">
              <Table className="table-sm mb-0 align-middle table-hover">
                <thead>
                  <tr>
                    <th style={{ width: 48 }}>#</th>
                    <th>{t.academic.attendance.colStudent}</th>
                    <th style={{ width: 100 }}>{t.academic.attendance.colRoom}</th>
                    <th style={{ width: 90 }}>{t.academic.absence.colRegularClass}</th>
                    <th style={{ width: 95 }}>{t.academic.leave.colType}</th>
                    <th style={{ width: 190 }}>{t.academic.leave.colDates}</th>
                    <th className="text-end" style={{ width: 70 }}>{t.academic.leave.colDays}</th>
                    <th>{t.academic.leave.colNote}</th>
                    <th style={{ width: 150 }}>{t.academic.leave.colReportedBy}</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((r, i) => {
                    const tone = TYPES.find(x => x.key === r.leave_type)
                    return (
                      <tr
                        key={`${r.source}-${r.leave_id}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/islamic/student-tracking/${r.st_id}`)}
                      >
                        <td className="text-secondary fs-xs">{i + 1}</td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <StudentAvatar photo={r.st_photo} name={studentDisplayName(r, lang)} size={28} />
                            <div style={{ minWidth: 0 }}>
                              <div className="fs-sm text-truncate"><StudentName student={r} lang={lang} /></div>
                              <div className="fs-xs text-secondary">{r.st_idnumber}</div>
                            </div>
                          </div>
                        </td>
                        <td className="fs-sm">{roomLabel(r)}</td>
                        <td className="fs-sm">{r.regular_class || <span className="text-secondary">-</span>}</td>
                        <td>
                          <span className="d-inline-flex align-items-center gap-1 fs-sm">
                            <StatusDot color={tone ? tone.color : '#6e7985'} size={8} />
                            {t.academic.leave.type[r.leave_type] || r.leave_type}
                          </span>
                        </td>
                        <td className="fs-sm">
                          {dateText(r)}
                          {r.returned_at && (
                            <div className="fs-xs text-success">{t.academic.leave.returnedAt(r.returned_at)}</div>
                          )}
                        </td>
                        <td className="text-end fs-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {r.days}
                          {r.total_days > r.days && (
                            <span className="text-secondary fs-xs"> / {r.total_days}</span>
                          )}
                        </td>
                        <td className="fs-sm">{r.leave_note || <span className="text-secondary">-</span>}</td>
                        <td className="fs-sm">
                          <ReportedBy t={t} row={r} />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
        <Card.Footer className="fs-xs text-secondary">{t.academic.leave.footerHint}</Card.Footer>
      </Card>
    </React.Fragment>
  )
}
